import { useState, useEffect } from 'react';
import { isEqual } from 'lodash';

import AggregateRecon from '../../components/renconcilationtype/AggregateRecon';
import {
    getSourceGroupByFromOldConfig, getTargetGroupByFromOldConfig,
    getUpdatedSrcGroupByList, getUpdatedTrgtGroupByList, getUpdatedSourceColumn,
    getUpdatedTargetColumn, getUpdatedSrcGrpByCols, getUpdatedTrgtGrpByCols,
    getUpdatedSrcRoundUp, getUpdatedTrgtRoundUp, shouldShowSrcGrpByList,
    shouldShowTrgtGrpByList, isValidConfig, buildConfig,
} from './utils';

const regForRound = '^(|-?[0-9]+)$';

const AggregateReconContainer = ({
    configData, saveData, oldConfig, setError, operation,
}) => {
    const [updatedConfigData, setUpdatedConfigData] = useState([]);
    const [sourceColumn, setSourceColumn] = useState([]);
    const [targetColumn, setTargetColumn] = useState([]);
    const [sourceGroupBy, setSourceGroupBy] = useState([]);
    const [targetGroupBy, setTargetGroupBy] = useState([]);
    const [srcGroupByList, setSrcGroupByList] = useState([]);
    const [trgtGroupByList, setTrgtGroupByList] = useState([]);
    const [sourceRoundUp, setSourceRoundUp] = useState('');
    const [targetRoundUp, setTargetRoundUp] = useState('');
    const [sourceRoundError, setSourceRoundError] = useState(false);
    const [targetRoundError, setTargetRoundError] = useState(false);
    const [shouldNotShowSrcGrpByList, setShouldNotShowSrcGrpByList] = useState(false);
    const [shouldNotShowTrgtGrpByList, setShouldNotShowTrgtGrpByList] = useState(false);
    /* function to save Sum reconciliation config */
    const onSaveConfig = () => {
        if (updatedConfigData.length > 0) {
            const config = buildConfig(updatedConfigData, operation, sourceGroupBy, targetGroupBy);
            let updatedConfig;
            if (operation === 'Sum') {
                updatedConfig = {
                    sourceRoundUp,
                    targetRoundUp,
                    ...config,
                };
            } else {
                updatedConfig = {
                    ...config,
                };
            }
            saveData({
                flow: {
                    ...updatedConfig,
                },
            });
        }
    };

    /* callback function called when sourceColumn is selected */
    const onSourceColumnSelect = event => {
        setUpdatedConfigData(prevState => {
            const data = [...prevState];
            const updateData = data[0].selectedColumns.map(sc => (
                sc.column === event ? { ...sc, isSourceColumn: true } : { ...sc, isSourceColumn: false }
            ));
            data[0].selectedColumns = updateData;
            return data;
        });
        if (event !== undefined) {
            setSourceColumn(event);
        }
    };

    /* callback function called when targetColumn is selected */
    const onTargetColumnSelect = event => {
        setUpdatedConfigData(prevState => {
            const data = [...prevState];
            const updateData = data[1].selectedColumns.map(sc => (
                sc.column === event ? { ...sc, isTargetColumn: true } : { ...sc, isTargetColumn: false }
            ));
            data[1].selectedColumns = updateData;
            return data;
        });
        if (event !== undefined) {
            setTargetColumn(event);
        }
    };

    /* callback function called when sourceGroupBy is selected */
    const onSourceGroupBy = event => {
        setUpdatedConfigData(prevState => {
            const data = [...prevState];
            const updateData = data[0].selectedColumns.map(sc => (
                event.includes(sc.column) ? { ...sc, isSourceGroupBy: true } : { ...sc, isSourceGroupBy: false }
            ));
            data[0].selectedColumns = updateData;
            return data;
        });
        if (event !== undefined) {
            const data = [].concat(event);
            setSourceGroupBy(data);
        }
    };

    /* callback function called when targetGroupBy is selected */
    const onTargetGroupBy = event => {
        setUpdatedConfigData(prevState => {
            const data = [...prevState];
            const updateData = data[1].selectedColumns.map(sc => (
                event.includes(sc.column) ? { ...sc, isTargetGroupBy: true } : { ...sc, isTargetGroupBy: false }
            ));
            data[1].selectedColumns = updateData;
            return data;
        });
        if (event !== undefined) {
            const data = [].concat(event);
            setTargetGroupBy(data);
        }
    };

    /* callback function called when sourceRoundUp is changed */
    const onSourceRoundUp = event => {
        if (event !== undefined) {
            setSourceRoundUp(event);
            if (!event.match(regForRound)) {
                setSourceRoundError(true);
            } else {
                setSourceRoundError(false);
            }
        }
    };

    /* callback function called when targetRoundUp is changed */
    const onTargetRoundUp = event => {
        if (event !== undefined) {
            setTargetRoundUp(event);
            if (!event.match(regForRound)) {
                setTargetRoundError(true);
            } else {
                setTargetRoundError(false);
            }
        }
    };

    /* side effect when configData is changed */
    useEffect(() => {
        const { message } = isValidConfig(configData);
        if (message !== '') {
            setError({ message });
        }
        if (!isEqual(updatedConfigData, configData)) {
            setUpdatedConfigData(configData);
        }
    }, [configData]);

    /* side effect when configData is changed */
    useEffect(() => {
        if (configData.length > 0) {
            setSrcGroupByList(getUpdatedSrcGroupByList(configData));
            setTrgtGroupByList(getUpdatedTrgtGroupByList(configData));
        }
    }, [configData]);

    /* side effect when updatedConfigData and oldConfig is changed */
    useEffect(() => {
        if (oldConfig && configData.length > 0) {
            const srcCol = oldConfig.sourceColumns[0];
            const targetCol = oldConfig.targetColumns[0];
            const srcgroupByCols = getSourceGroupByFromOldConfig(oldConfig, configData);
            const trgtgroupByCols = getTargetGroupByFromOldConfig(oldConfig, configData);
            const { sourceRoundUp: srcRoundUp, targetRoundUp: trgtRoundUp } = oldConfig;
            setSourceColumn(getUpdatedSourceColumn(sourceColumn, srcCol));
            setTargetColumn(getUpdatedTargetColumn(targetColumn, targetCol));
            setSourceGroupBy(getUpdatedSrcGrpByCols(srcgroupByCols, sourceGroupBy));
            setTargetGroupBy(getUpdatedTrgtGrpByCols(trgtgroupByCols, targetGroupBy));
            setSourceRoundUp(getUpdatedSrcRoundUp(sourceRoundUp, srcRoundUp));
            setTargetRoundUp(getUpdatedTrgtRoundUp(targetRoundUp, trgtRoundUp));
        }
    }, [oldConfig, configData]);

    /* side effect when sourceColumn, targetColumn,
* sourceGroupBy, targetGroupBy, sourceRoundUp, targetRoundUp,
* sourceRoundError, targetRoundError
* is changed */
    useEffect(() => {
        if (sourceColumn && sourceColumn.length > 0 && targetColumn && targetColumn.length > 0 && !sourceRoundError && !targetRoundError) {
            onSaveConfig();
        }
    }, [sourceColumn, targetColumn, sourceGroupBy, targetGroupBy, sourceRoundUp, targetRoundUp, sourceRoundError, targetRoundError]);

    /* side effect when updatedConfigData and srcGroupByList is changed */
    useEffect(() => {
        setShouldNotShowSrcGrpByList(shouldShowSrcGrpByList(srcGroupByList, updatedConfigData));
    }, [srcGroupByList, updatedConfigData]);

    /* side effect when updatedConfigData and trgtGroupByList is changed */
    useEffect(() => {
        setShouldNotShowTrgtGrpByList(shouldShowTrgtGrpByList(trgtGroupByList, updatedConfigData));
    }, [trgtGroupByList, updatedConfigData]);

    return (
        <AggregateRecon
            updatedConfigData={updatedConfigData}
            sourceColumn={sourceColumn}
            onSourceColumnSelect={onSourceColumnSelect}
            targetColumn={targetColumn}
            onTargetColumnSelect={onTargetColumnSelect}
            sourceGroupBy={sourceGroupBy}
            onSourceGroupBy={onSourceGroupBy}
            srcGroupByList={srcGroupByList}
            shouldNotShowSrcGrpByList={shouldNotShowSrcGrpByList}
            targetGroupBy={targetGroupBy}
            onTargetGroupBy={onTargetGroupBy}
            trgtGroupByList={trgtGroupByList}
            shouldNotShowTrgtGrpByList={shouldNotShowTrgtGrpByList}
            sourceRoundUp={sourceRoundUp}
            targetRoundUp={targetRoundUp}
            onSourceRoundUp={onSourceRoundUp}
            onTargetRoundUp={onTargetRoundUp}
            sourceRoundError={sourceRoundError}
            targetRoundError={targetRoundError}
            operation={operation}
        />
    );
};

export default AggregateReconContainer;
